import React, { useState } from "react";

function getHashSteps(nums, target) {
  const steps = [];
  const hash = {};

  // Record the initial empty hash
  steps.push({
    index: -1,
    hashSnapshot: { ...hash },
    message: "Start with an empty hash map."
  });

  // Iterate over the array
  for (let i = 0; i < nums.length; i++) {
    const currentValue = nums[i];
    const complement = target - currentValue;

    // Check if we already have the needed complement in the hash
    if (complement in hash) {
      // Found the pair
      steps.push({
        index: i,
        hashSnapshot: { ...hash },
        message: `Found complement ${complement} in the hash map. Pair: [${hash[complement]}, ${i}]`
      });
      break;
    } else {
      // Otherwise, store the current value with its index in the hash
      steps.push({
        index: i,
        hashSnapshot: { ...hash },
        message: `nums[${i}] = ${currentValue}; store {${currentValue}: ${i}} in the hash map.`
      });
      hash[currentValue] = i;
    }
  }

  return steps;
}

const HashAnimation = () => {
  // Define the array and target
  const nums = [1, 2, 3, 5, 9, 11, 6, 10, 12];
  const target = 22;

  const steps = getHashSteps(nums, target);
  const [stepIndex, setStepIndex] = useState(0);

  // Handlers for step navigation
  const handlePrevStep = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleNextStep = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  };

  // Extract info for the current step
  const currentStep = steps[stepIndex];
  const { index, hashSnapshot, message } = currentStep;

  return (
    <div className="p-4 bg-gray-100 dark:bg-gray-900">
      <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        Hash Animation Sample
      </h4>

      {/* Display the array, highlighting the current index */}
      <div className="flex space-x-4 mb-6">
        {nums.map((num, i) => {
          const bgColor = i === index ? "bg-green-600" : "bg-gray-200";
          return (
            <div
              key={i}
              className={`w-12 h-12 flex items-center justify-center ${bgColor} rounded shadow`}
            >
              {num}
            </div>
          );
        })}
      </div>

      {/* Step navigation buttons */}
      <div className="mb-4 space-x-2">
        <button
          onClick={handlePrevStep}
          disabled={stepIndex === 0}
          className={`px-4 py-2 rounded text-white ${
            stepIndex > 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400"
          }`}
        >
          Previous Step
        </button>
        <button
          onClick={handleNextStep}
          disabled={stepIndex === steps.length - 1}
          className={`px-4 py-2 rounded text-white ${
            stepIndex < steps.length - 1
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400"
          }`}
        >
          Next Step
        </button>
      </div>

      {/* Step information + snapshot of the hash map at this step */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow">
        <p className="text-gray-900 dark:text-gray-200">
          <strong>Step {stepIndex + 1} / {steps.length}:</strong> {message}
        </p>
        <div className="mt-2">
          <strong className="text-gray-900 dark:text-gray-200">Hash Map Snapshot:</strong>
          {Object.keys(hashSnapshot).length === 0 ? (
            <p className="mt-2 text-gray-600 dark:text-gray-300">Empty hash map</p>
          ) : (
            <div className="flex flex-wrap gap-4 mt-2 text-gray-600 dark:text-gray-300">
              {Object.entries(hashSnapshot).map(([key, val], idx) => (
                <div
                  key={idx}
                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded"
                >
                  {key}: {val}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HashAnimation;
